import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, AlertTriangle, ArrowRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Yataş Grubu · Davranış Haritası (/yatas/davranis-haritasi)
 * Davranış segmentasyonunun toplantıda gösterilecek anlatım sürümü:
 * üç soru (ne için geldi → nereden girdi → sonra ne yaptı), huni ile karşılaştırma, niyet satırları.
 * Veri: GET /api/behavior-segments?days=7|28|90 (segmentasyon sayfasıyla aynı uç ve önbellek).
 */

const API_BASE = (() => {
  const raw = (process.env.REACT_APP_API_URL || 'https://api.glowytics.com').trim();
  return raw.replace(/\/+$/, '').replace(/\/api$/, '');
})();

const C = {
  ground: '#F6F8FF', card: '#FFFFFF', line: '#E5E7EB', hair: '#F3F4F6',
  ink: '#111827', ink2: '#374151', muted: '#6B7280', faint: '#9CA3AF',
  accent: '#436AEB', accentSoft: '#EEF2FE', good: '#1E8E5A', bad: '#C8442F', badSoft: '#FDF1EE',
  neutral: '#A3AAC2', seg1: '#436AEB', seg2: '#8FA8F5', seg3: '#C9D5FA', seg4: '#E6EBFB'
};
const FONT = '-apple-system, BlinkMacSystemFont, "SF Pro Display", "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

const nf = (n, d = 0) => Number(n || 0).toLocaleString('tr-TR', { maximumFractionDigits: d, minimumFractionDigits: d });
const pct = (x, d) => {
  const v = (x || 0) * 100;
  const dd = d != null ? d : (v > 0 && v < 1 ? 2 : 1);
  return `%${nf(v, dd)}`;
};
const kisa = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return `${nf(v / 1e6, 2)} milyon`;
  if (v >= 1e4) return `${nf(v / 1e3)} bin`;
  return nf(v);
};
const AY = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const gun = (s) => (s ? `${Number(s.slice(8, 10))} ${AY[Number(s.slice(5, 7)) - 1]}` : '');

const kart = { background: C.card, border: `1px solid ${C.line}`, borderRadius: 12, boxShadow: '0 1px 2px rgba(16,24,40,.04)' };
const eyebrow = { fontSize: 11.5, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: C.accent };

// Giriş sayfası türleri: ilk üç tür renkli, gerisi "Diğer"
const SEG_RENK = [C.seg1, C.seg2, C.seg3];
// Ürün kategorileri: sitenin en büyük 6 kategorisi her satırda aynı renkte, gerisi gri
const CAT_RENK = ['#436AEB', '#1BAF7A', '#EDA100', '#9B59D0', '#EB6834', '#2BB3C0'];
const CAT_DIGER = '#C4C9D4';
const tlKisa = (n) => {
  const v = Number(n || 0);
  if (v >= 1e6) return `₺${nf(v / 1e6, 1)}M`;
  if (v >= 1e3) return `₺${nf(v / 1e3)}B`;
  return `₺${nf(v)}`;
};

/** Kategori ciro payı yığılı çubuğu + lejant */
function KategoriSeridi({ kategoriler, renk, adetli = true, enFazla = 3 }) {
  const top = kategoriler.reduce((a, c) => a + c.revenue, 0) || 1;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <div style={{ display: 'flex', height: 12, borderRadius: 4, overflow: 'hidden', background: C.hair }}
        title={kategoriler.map((c) => `${c.name}: ${pct(c.revenue / top, 0)} · ${nf(c.units)} adet`).join(' · ')}>
        {kategoriler.map((c) => <div key={c.name} style={{ width: `${(c.revenue / top) * 100}%`, background: renk(c.name) }} />)}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 14px', fontSize: 12.5, color: C.muted }}>
        {kategoriler.slice(0, enFazla).filter((c) => c.revenue / top >= 0.03).map((c) => (
          <span key={c.name}><i style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: renk(c.name), marginRight: 5 }} />
            {c.name} <b style={{ color: C.ink }}>{pct(c.revenue / top, 0)}</b>{adetli ? ` · ${nf(c.units)} adet` : ''}</span>
        ))}
      </div>
    </div>
  );
}

function Secici({ deger, onChange }) {
  return (
    <div style={{ display: 'inline-flex', background: C.hair, borderRadius: 10, padding: 3 }}>
      {[[7, '7 gün'], [28, '28 gün'], [90, '90 gün']].map(([k, label]) => (
        <button key={k} onClick={() => onChange(k)} style={{
          border: 0, cursor: 'pointer', fontFamily: FONT, fontSize: 13, padding: '6px 12px', borderRadius: 8,
          background: deger === k ? C.card : 'transparent', color: deger === k ? C.ink : C.muted,
          fontWeight: deger === k ? 600 : 500, boxShadow: deger === k ? '0 1px 2px rgba(16,24,40,.08)' : 'none'
        }}>{label}</button>
      ))}
    </div>
  );
}

function Bar({ w, renk, h = 10 }) {
  return (
    <div style={{ background: C.hair, borderRadius: h / 2, height: h, overflow: 'hidden' }}>
      <div style={{ width: `${Math.max(0, Math.min(100, w))}%`, height: h, borderRadius: h / 2, background: renk }} />
    </div>
  );
}

function Metrik({ etiket, w, renk, metin }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '112px minmax(0,1fr) 62px', gap: 10, alignItems: 'center', fontSize: 13 }}>
      <span style={{ color: C.ink2 }}>{etiket}</span>
      <Bar w={w} renk={renk} />
      <span style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{metin}</span>
    </div>
  );
}

/** Siteye göre niyetin hüküm cümlesi */
function hukum(it, ort) {
  const oran = ort.atcRate > 0 ? it.atcRate / ort.atcRate : 1;
  if (oran >= 1.5) return { ton: 'good', metin: `Ne istediğini biliyor: sepete ekleme site ortalamasının ${nf(oran, 1)} katı.` };
  if (it.bounceRate >= 0.5) return { ton: 'bad', metin: `Hemen çıkma oranı ${pct(it.bounceRate, 0)}: çoğu hiçbir şeye dokunmadan ayrılıyor.` };
  if (oran <= 0.6) return { ton: 'bad', metin: `Sepete ekleme site ortalamasının çok altında (${pct(it.atcRate)} / ${pct(ort.atcRate)}).` };
  if (oran >= 1.15) return { ton: 'good', metin: 'Ortalamanın üstünde: girdiği yerden ürüne ve sepete yürüyor.' };
  return { ton: 'mid', metin: 'Site ortalamasına yakın davranıyor.' };
}

function NiyetSatiri({ it, ort, maks, vurgu, satin, urunVar, catRenk }) {
  const lt = [...(it.byLandingType || [])].sort((a, b) => b.sessions - a.sessions);
  const ilk3 = lt.slice(0, 3);
  const toplam = it.sessions || 1;
  const terimler = (it.signals?.adsKeywords?.length ? it.signals.adsKeywords.map((k) => k.term) : (it.signals?.gscQueries || []).map((q) => q.query))
    .filter((t, i, a) => t && a.indexOf(t) === i).slice(0, 3);
  const kanallar = [...(it.byChannel || [])].sort((a, b) => b.sessions - a.sessions).slice(0, 2);
  const urun = (it.nextStepTypes || []).find((n) => n.type === 'product');
  const h = hukum(it, ort);
  const tonRenk = h.ton === 'good' ? C.good : h.ton === 'bad' ? C.bad : C.muted;
  const metrikRenk = h.ton === 'bad' ? C.bad : h.ton === 'good' ? C.good : C.accent;

  const kats = it.productCategories || [];
  return (
    <div style={{
      ...kart, padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 14,
      ...(vurgu ? { borderColor: C.bad, boxShadow: `inset 0 0 0 1px ${C.bad}` } : {})
    }}>
    <div className="dh-row">
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <div style={{ fontSize: 18, fontWeight: 700, color: C.ink, lineHeight: 1.2 }}>{it.label}</div>
        <div style={{ fontSize: 13, color: C.muted }}>{nf(it.sessions)} ziyaret · {pct(it.share, 0)}</div>
        {terimler.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {terimler.map((t) => (
              <span key={t} style={{ fontSize: 12, background: C.accentSoft, color: C.ink2, padding: '3px 9px', borderRadius: 999 }}>{t}</span>
            ))}
          </div>
        )}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <div style={{ display: 'flex', height: 22, borderRadius: 6, overflow: 'hidden', background: C.seg4 }}
          title={lt.map((x) => `${x.label}: ${pct(x.sessions / toplam, 0)}`).join(' · ')}>
          {ilk3.map((x, i) => <div key={x.type} style={{ width: `${(x.sessions / toplam) * 100}%`, background: SEG_RENK[i] }} />)}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '2px 12px', fontSize: 12.5, color: C.muted }}>
          {ilk3.filter((x) => x.sessions / toplam >= 0.03).map((x, i) => (
            <span key={x.type}><i style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: SEG_RENK[i], marginRight: 5 }} />
              {x.label} <b style={{ color: C.ink }}>{pct(x.sessions / toplam, 0)}</b></span>
          ))}
        </div>
        <div style={{ fontSize: 12.5, color: C.muted }}>{kanallar.map((k) => `${k.label} ${kisa(k.sessions)}`).join(' · ')}</div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <Metrik etiket="Sepete ekledi" w={(it.atcRate / maks.atc) * 100} renk={metrikRenk} metin={pct(it.atcRate)} />
        <Metrik etiket="Hemen çıktı" w={it.bounceRate * 100} renk={it.bounceRate >= 0.5 ? C.bad : C.neutral} metin={pct(it.bounceRate, 0)} />
        {satin
          ? <Metrik etiket="Satın aldı" w={(it.purchaseRate / maks.purchase) * 100} renk={metrikRenk} metin={pct(it.purchaseRate, 2)} />
          : <Metrik etiket="Ürüne geçiş*" w={((urun?.per100 || 0) / maks.urun) * 100} renk={metrikRenk} metin={nf(urun?.per100 || 0, 1)} />}
        <div style={{ fontSize: 13, fontWeight: 600, color: tonRenk }}>{h.metin}</div>
      </div>
    </div>
    {satin && (
      <div className="dh-buy" style={{ borderTop: `1px dashed ${C.line}`, paddingTop: 12 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', alignItems: 'baseline', fontSize: 13, color: C.muted }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: C.accent }}>Ne aldı</span>
          <span>Ciro payı <b style={{ color: C.ink, fontSize: 15 }}>{pct(ort.revenue > 0 ? it.revenue / ort.revenue : 0)}</b></span>
          <span><b style={{ color: C.ink }}>{tlKisa(it.revenue)}</b> ciro</span>
          <span><b style={{ color: C.ink }}>{nf(it.purchases)}</b> sipariş</span>
          {urunVar && <span><b style={{ color: C.ink }}>{nf(it.units)}</b> adet</span>}
        </div>
        {urunVar && kats.length > 0 && <KategoriSeridi kategoriler={kats} renk={catRenk} />}
      </div>
    )}
    </div>
  );
}

export default function YatasDavranisHaritasi() {
  const { selectedBrand } = useAuth();
  const brandId = selectedBrand?.id || (typeof window !== 'undefined' ? localStorage.getItem('currentBrandId') : null);
  const [days, setDays] = useState(28);
  const [veri, setVeri] = useState(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [sn, setSn] = useState(0);

  const yukle = useCallback(async () => {
    if (!brandId) return;
    setYukleniyor(true); setHata(null); setSn(0);
    try {
      const res = await fetch(`${API_BASE}/api/behavior-segments?days=${days}`, {
        credentials: 'include', headers: { 'Content-Type': 'application/json', 'X-Brand-ID': brandId }
      });
      const j = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(j.error || j.message || `HTTP ${res.status}`);
      setVeri(j);
    } catch (e) {
      setVeri(null);
      setHata(e.message);
    } finally {
      setYukleniyor(false);
    }
  }, [brandId, days]);

  useEffect(() => { setVeri(null); yukle(); }, [yukle]);
  useEffect(() => {
    if (!yukleniyor) return undefined;
    const t = setInterval(() => setSn((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [yukleniyor]);

  const meta = veri?.meta;
  const tot = veri?.totals;
  const satin = meta?.hasPurchaseData !== false;

  // %1 ve üstü paya sahip, niyeti belli gruplar
  const niyetler = useMemo(() => (veri?.intents || [])
    .filter((i) => i.key !== 'other' && (i.share || 0) >= 0.01)
    .sort((a, b) => b.sessions - a.sessions), [veri]);
  const belirsiz = (veri?.intents || []).find((i) => i.key === 'other');
  const enKotu = useMemo(() => niyetler.reduce((m, i) => (!m || i.atcRate < m.atcRate ? i : m), null), [niyetler]);
  const maks = useMemo(() => ({
    atc: Math.max(...niyetler.map((i) => i.atcRate), 0.0001),
    purchase: Math.max(...niyetler.map((i) => i.purchaseRate), 0.0001),
    urun: Math.max(...niyetler.map((i) => (i.nextStepTypes || []).find((n) => n.type === 'product')?.per100 || 0), 0.0001)
  }), [niyetler]);

  // Örnek ziyaretçi: en çok aranan reklam terimi olan, marka dışı niyet
  const ornek = useMemo(() => {
    const aday = niyetler
      .filter((i) => i.key !== 'brand_search' && i.signals?.adsKeywords?.length && i.topLandings?.length)
      .sort((a, b) => (b.signals.adsKeywords[0].sessions || 0) - (a.signals.adsKeywords[0].sessions || 0))[0];
    if (!aday) return null;
    const giris = aday.topLandings[0];
    const sonra = (aday.topNextSteps || []).find((n) => n.type === 'product') || aday.topNextSteps?.[0];
    return { niyet: aday, terim: aday.signals.adsKeywords[0].term, giris: giris.label, sonra: sonra?.label };
  }, [niyetler]);

  const urunVar = !!meta?.hasItemData && satin;
  const kategoriler = useMemo(() => veri?.productCategories || [], [veri]);
  const catRenk = useMemo(() => {
    const m = new Map(kategoriler.slice(0, CAT_RENK.length).map((c, i) => [c.name, CAT_RENK[i]]));
    return (name) => m.get(name) || CAT_DIGER;
  }, [kategoriler]);
  // Ürün kategorisi tablosu: ilk 10 + kalanlar tek satırda
  const katTablo = useMemo(() => {
    const ilk = kategoriler.slice(0, 10);
    const kalan = kategoriler.slice(10);
    if (!kalan.length) return ilk;
    return [...ilk, {
      name: `Diğer ${kalan.length} kategori`, diger: true,
      units: kalan.reduce((a, c) => a + c.units, 0), revenue: kalan.reduce((a, c) => a + c.revenue, 0),
      revenueShare: kalan.reduce((a, c) => a + c.revenueShare, 0)
    }];
  }, [kategoriler]);
  const niyetCiro = useMemo(() => [...(veri?.intents || [])].filter((i) => i.revenue > 0).sort((a, b) => b.revenue - a.revenue), [veri]);
  const gscKat = meta?.organicVsGsc?.gscClicks > 0 ? meta.organicVsGsc.ga4OrganicSessions / meta.organicVsGsc.gscClicks : null;
  const funnel = tot ? [
    { etiket: 'Ziyaret', deger: tot.sessions, oran: null, w: 100 },
    { etiket: 'Sepete ekleme', deger: tot.atc, oran: tot.atcRate, w: 64 },
    ...(satin ? [{ etiket: 'Satın alma', deger: tot.purchases, oran: tot.purchaseRate, w: 32 }] : [])
  ] : [];

  return (
    <div style={{ background: C.ground, minHeight: '100%', padding: '28px 28px 56px', fontFamily: FONT, color: C.ink }}>
      <style>{`
        .dh-wrap { max-width: 1180px; margin: 0 auto; display: flex; flex-direction: column; gap: 36px; }
        .dh-3 { display: grid; grid-template-columns: repeat(3, minmax(0,1fr)); gap: 14px; }
        .dh-vs { display: grid; grid-template-columns: minmax(0,1fr) minmax(0,1.35fr); gap: 14px; }
        .dh-buy { display: grid; grid-template-columns: minmax(0,1.05fr) minmax(0,2.55fr); gap: 22px; align-items: center; }
        .dh-cat { display: grid; grid-template-columns: minmax(130px,1.2fr) minmax(0,2fr) 64px 84px 72px; gap: 12px; align-items: center; font-size: 13px; }
        .dh-row, .dh-head { display: grid; grid-template-columns: minmax(0,1.05fr) minmax(0,1.2fr) minmax(0,1.35fr); gap: 22px; align-items: start; }
        .dh-path { display: grid; grid-template-columns: repeat(4, minmax(0,1fr)); gap: 28px; }
        .dh-step { position: relative; }
        .dh-step:not(:last-child)::after { content: ""; position: absolute; right: -20px; top: 50%; width: 12px; height: 12px;
          border-top: 3px solid ${C.accent}; border-right: 3px solid ${C.accent}; transform: translateY(-50%) rotate(45deg); }
        @media (max-width: 960px) {
          .dh-3, .dh-vs, .dh-row, .dh-path, .dh-buy { grid-template-columns: 1fr; }
          .dh-cat { grid-template-columns: minmax(100px,1fr) minmax(0,1.2fr) 52px 70px; }
          .dh-cat .dh-opt { display: none; }
          .dh-head { display: none; }
          .dh-step:not(:last-child)::after { right: auto; left: 22px; top: auto; bottom: -20px; transform: rotate(135deg); }
        }
        @keyframes dhSpin { to { transform: rotate(360deg); } }
      `}</style>
      <div className="dh-wrap">

        <header style={{ display: 'flex', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', alignItems: 'flex-start' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 760 }}>
            <span style={eyebrow}>
              {selectedBrand?.name || 'Yataş'}{meta?.range ? ` · ${gun(meta.range.start)} – ${gun(meta.range.end)} ${meta.range.end.slice(0, 4)}` : ''}
            </span>
            <h1 style={{ margin: 0, fontSize: 34, fontWeight: 700, letterSpacing: -0.6, lineHeight: 1.1 }}>
              Siteye gelen biri hakkında üç soru soruyoruz
            </h1>
            <p style={{ margin: 0, fontSize: 16, color: C.muted, lineHeight: 1.5 }}>
              Klasik huni kaç kişinin düştüğünü söyler. Bu sayfa <b style={{ color: C.ink }}>kimin, ne için geldiğini</b> ve
              siteye girdikten sonra nereye yürüdüğünü gösteriyor.
            </p>
          </div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Secici deger={days} onChange={setDays} />
            <Link to="/yatas/davranis-segmentasyonu" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: C.accent, textDecoration: 'none' }}>
              Detaylı analiz <ArrowRight size={14} />
            </Link>
          </div>
        </header>

        <section className="dh-3">
          {[
            ['1.', 'Ne için geldi?', "Google'da ne aradı, hangi kampanyaya tıkladı. Buna niyet diyoruz: yatak, bebek ürünü, bel ağrısı, marka adı…"],
            ['2.', 'Nereden girdi?', 'Siteye ilk açtığı sayfa: anasayfa mı, kategori mi, doğrudan bir ürün mü.'],
            ['3.', 'Sonra ne yaptı?', 'Ürüne geçti mi, sepete ekledi mi, satın aldı mı, yoksa hiçbir şeye dokunmadan çıktı mı.']
          ].map(([n, k, a]) => (
            <div key={k} style={{ ...kart, padding: 20, display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ fontSize: 20, fontWeight: 700 }}><span style={{ color: C.accent }}>{n}</span> {k}</div>
              <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.5 }}>{a}</div>
            </div>
          ))}
        </section>

        {yukleniyor && !veri && (
          <div style={{ ...kart, padding: 36, textAlign: 'center' }}>
            <RefreshCw size={24} color={C.accent} style={{ animation: 'dhSpin 1s linear infinite' }} />
            <div style={{ fontSize: 15, fontWeight: 600, marginTop: 12 }}>GA4&apos;ten hesaplanıyor</div>
            <div style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>İlk açılış 20 sn kadar sürebilir{days === 90 ? ', 90 gün daha uzun sürer' : ''}. Geçen süre: {sn} sn.</div>
          </div>
        )}
        {hata && (
          <div style={{ ...kart, padding: 20, display: 'flex', gap: 10, alignItems: 'center', borderColor: C.bad, color: C.bad }}>
            <AlertTriangle size={18} /> Veri alınamadı: {hata}
          </div>
        )}
        {veri?.empty && (
          <div style={{ ...kart, padding: 24, color: C.muted }}>Bu marka için veri yok: {veri.reason}</div>
        )}

        {tot && niyetler.length > 0 && (<>
          {ornek && (
            <section style={{ ...kart, padding: 22, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <span style={eyebrow}>Örnek bir ziyaretçi</span>
              <div className="dh-path">
                {[
                  ['Niyet', `Google'da “${ornek.terim}” aradı`],
                  ['Giriş', `${ornek.giris} sayfasına girdi`],
                  ['Sonra', ornek.sonra ? `${ornek.sonra} ürününe geçti` : 'Bir ürüne geçti'],
                  ['Sonuç', `Bu grupta sepete ekleme oranı ${pct(ornek.niyet.atcRate)}`]
                ].map(([t, v], i) => (
                  <div key={t} className="dh-step" style={{
                    padding: '14px 16px', borderRadius: 10, background: i === 3 ? C.accent : C.accentSoft,
                    color: i === 3 ? '#fff' : C.ink, display: 'flex', flexDirection: 'column', gap: 4
                  }}>
                    <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: i === 3 ? 'rgba(255,255,255,.8)' : C.accent }}>{t}</span>
                    <span style={{ fontSize: 15, fontWeight: 600, lineHeight: 1.35 }}>{v}</span>
                  </div>
                ))}
              </div>
              <div style={{ fontSize: 13, color: C.muted }}>
                Aynı niyetle gelen herkesi bir grupta topluyoruz ({ornek.niyet.label}: {nf(ornek.niyet.sessions)} ziyaret). Sonra grupları birbiriyle karşılaştırıyoruz.
              </div>
            </section>
          )}

          <section className="dh-vs">
            <div style={{ ...kart, padding: 22, display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div style={{ fontSize: 17, fontWeight: 700 }}>Huni ne söyler?</div>
              <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.5 }}>
                {meta.range.days} günde {kisa(tot.sessions)} ziyaret. Kaç kişinin nerede düştüğü görünür, ama <b style={{ color: C.ink }}>kimin</b> düştüğü görünmez.
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'center' }}>
                {funnel.map((f) => (
                  <div key={f.etiket} style={{ width: `${f.w}%`, background: C.seg3, borderRadius: 6, padding: '8px 10px', textAlign: 'center', fontSize: 13.5 }}>
                    {f.etiket} <b style={{ fontVariantNumeric: 'tabular-nums' }}>{nf(f.deger)}</b>{f.oran != null ? ` · ${pct(f.oran)}` : ''}
                  </div>
                ))}
              </div>
            </div>
            <div style={{ ...kart, padding: 22, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div style={{ fontSize: 17, fontWeight: 700 }}>Niyete göre bölünce ne görünür?</div>
              <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.5 }}>
                Aynı {pct(tot.atcRate)} ortalama, içinde çok farklı davranan grupları saklıyor. Sepete ekleme oranı:
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
                {[...niyetler].sort((a, b) => b.atcRate - a.atcRate).map((i) => (
                  <div key={i.key} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px,190px) minmax(0,1fr) 56px', gap: 10, alignItems: 'center', fontSize: 13 }}>
                    <span style={{ color: C.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.label}>{i.label}</span>
                    <Bar w={(i.atcRate / maks.atc) * 100} renk={i.key === enKotu?.key ? C.bad : C.accent} h={12} />
                    <span style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{pct(i.atcRate)}</span>
                  </div>
                ))}
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px,190px) minmax(0,1fr) 56px', gap: 10, alignItems: 'center', fontSize: 13, borderTop: `1px dashed ${C.line}`, paddingTop: 7 }}>
                  <span style={{ color: C.muted }}>Site ortalaması</span>
                  <Bar w={(tot.atcRate / maks.atc) * 100} renk={C.neutral} h={12} />
                  <span style={{ textAlign: 'right', fontWeight: 600, color: C.muted }}>{pct(tot.atcRate)}</span>
                </div>
              </div>
            </div>
          </section>

          {satin && (
            <section className="dh-vs">
              {urunVar && (
                <div style={{ ...kart, padding: 22, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0 }}>
                  <div style={{ fontSize: 17, fontWeight: 700 }}>Ne satıldı? Kategorilerin ciro payı ve satış adedi</div>
                  <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.5 }}>
                    {meta.range.days} günde {nf(kategoriler.reduce((a, c) => a + c.units, 0))} adet ürün, {tlKisa(kategoriler.reduce((a, c) => a + c.revenue, 0))} ürün cirosu.
                  </div>
                  <div className="dh-cat" style={{ fontSize: 11, fontWeight: 600, letterSpacing: '.08em', textTransform: 'uppercase', color: C.faint }}>
                    <span>Kategori</span><span>Ciro payı</span><span style={{ textAlign: 'right' }}>Pay</span>
                    <span style={{ textAlign: 'right' }}>Ciro</span><span className="dh-opt" style={{ textAlign: 'right' }}>Adet</span>
                  </div>
                  {katTablo.map((c) => (
                    <div key={c.name} className="dh-cat">
                      <span style={{ color: c.diger ? C.muted : C.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={c.name}>
                        <i style={{ display: 'inline-block', width: 8, height: 8, borderRadius: 2, background: c.diger ? CAT_DIGER : catRenk(c.name), marginRight: 6 }} />{c.name}
                      </span>
                      <Bar w={(c.revenueShare / (katTablo[0]?.revenueShare || 1)) * 100} renk={c.diger ? CAT_DIGER : catRenk(c.name)} h={12} />
                      <span style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{pct(c.revenueShare)}</span>
                      <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{tlKisa(c.revenue)}</span>
                      <span className="dh-opt" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{nf(c.units)}</span>
                    </div>
                  ))}
                </div>
              )}
              <div style={{ ...kart, padding: 22, display: 'flex', flexDirection: 'column', gap: 12, minWidth: 0, ...(urunVar ? {} : { gridColumn: '1 / -1' }) }}>
                <div style={{ fontSize: 17, fontWeight: 700 }}>Ciro hangi niyetten geliyor?</div>
                <div style={{ fontSize: 14, color: C.muted, lineHeight: 1.5 }}>Sitenin {tlKisa(tot.revenue)} cirosunun niyet gruplarına dağılımı.</div>
                {niyetCiro.map((i) => (
                  <div key={i.key} style={{ display: 'grid', gridTemplateColumns: 'minmax(120px,1.3fr) minmax(0,2fr) 52px 70px', gap: 10, alignItems: 'center', fontSize: 13 }}>
                    <span style={{ color: i.key === 'other' ? C.muted : C.ink2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={i.label}>{i.label}</span>
                    <Bar w={(i.revenue / (niyetCiro[0]?.revenue || 1)) * 100} renk={i.key === 'other' ? C.neutral : C.accent} h={12} />
                    <span style={{ textAlign: 'right', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{pct(tot.revenue > 0 ? i.revenue / tot.revenue : 0, 0)}</span>
                    <span style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: C.muted }}>{tlKisa(i.revenue)}</span>
                  </div>
                ))}
              </div>
            </section>
          )}

          <section style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>{niyetler.length} niyet, aynı üç soru</h2>
              <div style={{ fontSize: 14, color: C.muted }}>Her satır bir niyet grubu. Soldan sağa: ne için geldi, nereden girdi, sonra ne yaptı. Payı %1'in altındaki gruplar gösterilmiyor.</div>
            </div>
            <div className="dh-head" style={{ padding: '0 20px', fontSize: 11.5, fontWeight: 600, letterSpacing: '.1em', textTransform: 'uppercase', color: C.muted }}>
              <span>Ne için geldi</span><span>Nereden girdi</span><span>Sonra ne yaptı</span>
            </div>
            {niyetler.map((it) => (
              <NiyetSatiri key={it.key} it={it} ort={tot} maks={maks} vurgu={it.key === enKotu?.key} satin={satin} urunVar={urunVar} catRenk={catRenk} />
            ))}
            {!satin && (
              <div style={{ fontSize: 12.5, color: C.muted }}>
                Bu markada GA4 satın alma verisi yok, satın alma yerine ürüne geçiş gösteriliyor. * Ürüne geçiş: o gruptan 100 ziyarette girişten sonra açılan ürün sayfası sayısı; gruplar arası karşılaştırma içindir.
              </div>
            )}
          </section>

          <section style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Okurken üç şeye dikkat</h2>
            <div className="dh-3">
              {[
                ['“Sonra ne yaptı” bir kişinin yolu değil', 'O sayfadan sonra açılan sayfaların toplamı. “Bu kapıdan girenlerin çoğu şu odaya geçti” diye okunmalı, tek bir müşterinin adım adım yolculuğu diye değil.'],
                ['Organik aramada niyet tahmini', `Google organik aramada kelimeyi vermiyor. Niyeti Search Console sorgularından tahmin ediyoruz.${gscKat ? ` Organik ziyaret sayıları şişkin (GSC tıklamasının ~${nf(gscKat, 1)} katı), oranlar daha güvenilir.` : ''}`],
                [`Niyeti belirsiz ziyaret payı: ${pct(belirsiz?.share || 0, 0)}`, `${kisa(belirsiz?.sessions || 0)} ziyaret, çoğu reklamla doğrudan bir ürün sayfasına gelen trafik. Ürün adına göre eşleme eklenirse bu pay küçülür.`]
              ].map(([b, a]) => (
                <div key={b} style={{ borderTop: `3px solid ${C.neutral}`, paddingTop: 12, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ fontSize: 15, fontWeight: 700 }}>{b}</div>
                  <div style={{ fontSize: 13.5, color: C.muted, lineHeight: 1.5 }}>{a}</div>
                </div>
              ))}
            </div>
          </section>

          <div style={{ fontSize: 12, color: C.faint, borderTop: `1px solid ${C.line}`, paddingTop: 12 }}>
            Kaynak: GA4 (canlı, yalnız okuma) + Search Console · {meta.cached ? 'önbellekten' : 'yeni hesaplandı'} · {meta.generatedAt ? new Date(meta.generatedAt).toLocaleString('tr-TR') : ''}
          </div>
        </>)}
      </div>
    </div>
  );
}
